import React, { useState, useEffect } from "react";
import { motion, type Variants } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Leaf,
  Star,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router";

// Main Hero component combining the text and a dynamic image slider
const HeroComponent2 = () => {
  // Array of slides, each with a heading, subheading, and image URL
  const slides = [
    {
      heading: "Crafting Outdoor Spaces That Inspire",
      subheading:
        "Transform your property with our expert landscaping services, from initial design to flawless execution.",
      imageUrl:
        "https://images.unsplash.com/photo-1723031751229-0228cc91f7c9?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8N3x8Q3JhZnRpbmclMjBPdXRkb29yJTIwU3BhY2VzJTIwVGhhdCUyMEluc3BpcmV8ZW58MHx8MHx8fDA%3D",
    },
    {
      heading: "Seamless Design, Effortless Living",
      subheading:
        "Experience the fusion of natural beauty and modern functionality with our award-winning designs.",
      imageUrl:
        "https://images.unsplash.com/photo-1643267514395-b36b3f7e8281?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTF8fFNlYW1sZXNzJTIwRGVzaWduJTJDJTIwRWZmb3J0bGVzcyUyMExpdmluZ3xlbnwwfHwwfHx8MA%3D%3D",
    },
    {
      heading: "Your Vision, Our Expertise",
      subheading:
        "Let us bring your dream landscape to life with precision, passion, and sustainable practices.",
      imageUrl:
        "https://plus.unsplash.com/premium_photo-1681881315986-ee48079ebc9f?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTN8fFlvdXIlMjBWaXNpb24lMkMlMjBPdXIlMjBFeHBlcnRpc2UlMjBDcmFmdGluZyUyME91dGRvb3J8ZW58MHx8MHx8fDA%3D",
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  // Animation variants for the text and buttons
  const textVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  // Automatic slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === slides.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(timer);
  }, [slides.length]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === slides.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? slides.length - 1 : prevIndex - 1
    );
  };

  return (
    <section className="relative w-full min-h-screen bg-gray-50 flex items-center py-20 overflow-hidden">
      {/* Background Blobs for a dynamic effect */}
      <motion.div
        className="absolute top-1/4 left-0 w-80 h-80 bg-green-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"
        animate={{ y: [0, -30, 0], x: [0, 30, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-1/4 right-0 w-96 h-96 bg-green-400/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"
        animate={{ y: [0, 40, 0], x: [0, -40, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          {/* Content Section */}
          <motion.div
            initial="hidden"
            animate="visible"
            className="mb-12 lg:mb-0 text-center lg:text-left"
          >
            <motion.h1
              key={slides[currentIndex].heading}
              variants={textVariants}
              className="mb-4 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl"
            >
              {slides[currentIndex].heading}
            </motion.h1>
            <motion.p
              key={slides[currentIndex].subheading}
              variants={textVariants}
              className="mb-8 text-lg leading-relaxed text-gray-600 max-w-xl mx-auto lg:mx-0"
            >
              {slides[currentIndex].subheading}
            </motion.p>
            <motion.div
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8"
            >
              <Link to={"/contact"}>
                <div className="inline-flex items-center justify-center px-8 py-3 text-white bg-green-600 rounded-full shadow-lg hover:bg-green-700 transition-colors group">
                  Get a Free Estimate
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
              <Link to={"/portfolio"}>
                <div className="inline-flex items-center justify-center px-8 py-3 text-gray-700 bg-white rounded-full border border-gray-300 hover:bg-gray-100 transition-colors">
                  View Our Portfolio
                </div>
              </Link>
            </motion.div>
            <motion.div
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.3 }}
              className="flex justify-center lg:justify-start gap-6"
            >
              <div className="flex items-center space-x-2">
                <Leaf className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium text-gray-600">
                  Eco-Friendly Designs
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Star className="w-5 h-5 text-yellow-400" />
                <span className="text-sm font-medium text-gray-600">
                  Award-Winning Service
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* Hero Image Slider */}
          <motion.div
            initial="hidden"
            animate="visible"
            className="mt-12 lg:mt-0 flex justify-center lg:justify-end"
          >
            <div className="relative w-full max-w-lg lg:max-w-none h-[500px] rounded-3xl overflow-hidden shadow-2xl group">
              {/* This is the new, non-animated image display */}
              <img
                src={slides[currentIndex].imageUrl}
                alt={`Landscape image ${currentIndex + 1}`}
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* <ImagePlaceholder width={576} height={500} /> */}
              {/* Navigation Controls */}
              <div className="absolute inset-y-0 left-0 flex items-center p-4 z-10 transition-opacity duration-300 opacity-0 group-hover:opacity-100">
                <button
                  onClick={handlePrev}
                  className="rounded-full bg-white/80 hover:bg-gray-400 text-black backdrop-blur-sm p-2 transition-colors"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center p-4 z-10 transition-opacity duration-300 opacity-0 group-hover:opacity-100">
                <button
                  onClick={handleNext}
                  className="rounded-full bg-white/80 hover:bg-gray-400 text-black backdrop-blur-sm p-2 transition-colors"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>

              {/* Slide Indicators */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentIndex
                        ? "bg-white scale-125"
                        : "bg-white/50"
                    }`}
                    onClick={() => setCurrentIndex(index)}
                  ></button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroComponent2;
